import { getAllDistributionPoints } from "api/generated";
import {
  getSelectedDistributionPoint,
  saveSelectedDistributionPoint,
} from "common/components/widgets/DistributionPoint/distributionPointStorage";
import { useClient } from "hooks/useClient";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { getAllowedDistributionPoints } from "security";
import DistributionPointSelected, {
  DistributionPoint,
} from "context/MyDistributionPoint";

interface UseDistributionPointsResult {
  userDistributionPoints: DistributionPoint[];
  isLoading: boolean;
  onSelectDistributionPointId: (id: string) => void;
  distributionPoint: DistributionPoint | undefined;
  setDistributionPoint: Dispatch<SetStateAction<DistributionPoint>>;
}

export function useDistributionPoints(): UseDistributionPointsResult {
  const [userDistributionPoints, setUserDistributionPoints] = useState<
    DistributionPoint[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const { client } = useClient();
  const context = useContext(DistributionPointSelected);

  if (!context) {
    throw new Error(
      "useDistributionPoints debe usarse dentro de un MyDistributionPoint provider",
    );
  }
  const { distributionPoint, setDistributionPoint } = context;

  function saveSelectedDPP(id: string, name: string) {
    setDistributionPoint({ id, name });
    saveSelectedDistributionPoint(id, name);
    window.dispatchEvent(new Event("distributionPointChange"));
  }

  function onSelectDistributionPointId(id: string) {
    const selected = userDistributionPoints.find((p) => p.id === id);
    if (selected?.id && selected.name) {
      saveSelectedDPP(selected.id, selected.name);
    }
  }

  useEffect(() => {
    async function loadCatalogs() {
      setIsLoading(true);
      try {
        const allowedPoints = await getAllowedDistributionPoints;
        const storedId =
          distributionPoint?.id ?? getSelectedDistributionPoint() ?? "";

        if (!allowedPoints?.length) {
          getAllDistributionPoints({ client })
            .then(({ data }) => {
              const allDistributionPoints = data ?? [];

              // Determinar el ID a usar (el almacenado si existe, sino el primero)
              const selectedId = allDistributionPoints.some(
                (p) => p.id === storedId,
              )
                ? storedId
                : allDistributionPoints[0]?.id || "";

              setUserDistributionPoints(
                allDistributionPoints.map((dp) => {
                  return { id: dp.id ?? "", name: dp.subsidiary ?? "" };
                }),
              );

              // Guardar el punto seleccionado si existe
              const selectedPoint = allDistributionPoints.find(
                (p) => p.id === selectedId,
              );
              if (selectedPoint) {
                saveSelectedDPP(selectedId, selectedPoint.name ?? "");
              }
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else {
          setUserDistributionPoints(allowedPoints);

          // Actualizar el punto de distribución global
          saveSelectedDPP(allowedPoints[0].id, allowedPoints[0].name);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error loading distribution points:", error);
        setIsLoading(false);
      }
    }

    loadCatalogs();
  }, [client]);

  return {
    userDistributionPoints,
    isLoading,
    onSelectDistributionPointId,
    distributionPoint,
    setDistributionPoint,
  };
}
